import React, { useEffect, useState, useRef, useCallback } from 'react';
import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';
import { useToast } from 'context/providers/ToastProvider';
import { Card, Col, Row, Spinner, Badge, Table } from 'react-bootstrap';
import 'react-loading-skeleton/dist/skeleton.css';

const SeccionLogsAkasia = ({ idLicencia, uuidLicencia }) => {
    const { token } = useToken();
    const { showErrorToast } = useToast();

    const [logServicios, setLogServicios] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const observerTarget = useRef(null);
    const pageSize = 50;

    /**
     * Obtiene los datos de Logs desde el microservicio
     */
    const ObtieneSeccionLogsMicroservicio = useCallback(async (page = 1, append = false) => {
        // If we don't have the UUID, we might have to fallback to something else, 
        // or just wait until it's loaded.
        if (!uuidLicencia && !idLicencia) return;

        try {
            if (append) {
                setIsLoadingMore(true);
            }

            // Preferimos uuidLicencia, pero si no existe, mandamos el idLicencia como fallback
            const uuidQuery = uuidLicencia || idLicencia;
            const response = await fetch(`/api/licencia/ObtieneLogsMicroservicio/?uuidLicencia=${uuidQuery}&page=${page}&pageSize=${pageSize}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = "";
                    return;
                }
                throw new Error(`${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            if (data.iResponseType == 1) {
                let allLogs = [];
                try {
                    const ObjDetalles = JSON.parse(data.detalles);
                    // Standard ResponseType from Akasia Logs Web Service
                    // The actual array of logs is directly the array if it returned List<LogEntry>
                    if (Array.isArray(ObjDetalles)) {
                        allLogs = ObjDetalles;
                    } else if (ObjDetalles.table2) {
                        allLogs = ObjDetalles.table2;
                    }
                } catch (e) {
                    console.error("Error parsing logs JSON", e);
                }

                if (append) {
                    setLogServicios(prev => [...prev, ...allLogs]);
                } else {
                    setLogServicios(allLogs);
                }

                // If we got exactly the page size, there MIGHT be more logs
                // If we got less, we definitely reached the end.
                setHasMore(allLogs.length === pageSize);
            } else {
                setHasMore(false);
            }

        } catch (error) {
            showErrorToast("Error al cargar los logs desde el microservicio: " + error.message);
            console.error('Error al obtener datos de los logs:', error);
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
        }
    }, [idLicencia, uuidLicencia, token, showErrorToast, pageSize]); 

    /**
     * Observer para Infinite Scroll
     */
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
                    const nextPage = pageNumber + 1;
                    setPageNumber(nextPage);
                    ObtieneSeccionLogsMicroservicio(nextPage, true);
                }
            },
            { threshold: 1.0 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current);
            }
        };
    }, [pageNumber, hasMore, isLoadingMore, ObtieneSeccionLogsMicroservicio]);

    useEffect(() => {
        // Reset cuando cambia la licencia
        setLogServicios([]);
        setPageNumber(1);
        setHasMore(true);
        if (uuidLicencia || idLicencia) {
            ObtieneSeccionLogsMicroservicio(1, false);
        }
    }, [idLicencia, uuidLicencia, ObtieneSeccionLogsMicroservicio]);

    return (
        <Row className="g-3 mb-3">
            <Col lg={12}>
                <Card className="mb-3" >
                    <Card.Header className="bg-light">
                        <h5 className="mb-0 text-dark">Logs Activos</h5>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'auto', padding: '1rem', boxSizing: 'border-box' }}>
                            
                            <Table responsive hover size="sm" className="mb-0 fs--1">
                                <thead className="sticky-top bg-white" style={{ zIndex: 2, top: '-1rem' }}>
                                    <tr>
                                        <th className="text-muted fw-bold">Fecha</th>
                                        <th className="text-muted fw-bold">Usuario / Módulo / Versión</th>
                                        <th className="text-muted fw-bold">IP</th>
                                        <th className="text-muted fw-bold">Mensaje</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logServicios.length === 0 && !isLoadingMore ? (
                                        <tr>
                                            <td colSpan="4" className="text-center text-muted py-5">
                                                No hay logs registrados en el microservicio.
                                            </td>
                                        </tr>
                                    ) : (
                                        logServicios.map((log, index) => (
                                            <tr key={index}>
                                                <td className="align-middle text-nowrap">
                                                    {(() => {
                                                        try {
                                                            const d = new Date(log.createdDate);
                                                            const dateStr = d.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
                                                             const timeStr = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
                                                            return (
                                                                <>
                                                                    <div>{dateStr}</div>
                                                                    <div>{timeStr}</div>
                                                                </>
                                                            );
                                                        } catch (e) {
                                                            return log.createdDate;
                                                        }
                                                    })()}
                                                </td>
                                                <td className="align-middle">
                                                    <div className="mb-1">{log.usuario || log.createdUser || '-'}</div>
                                                    <div>
                                                        <Badge bg="info" className="fw-normal me-1">{log.modulo || 'Desconocido'}</Badge>
                                                        {log.version && log.version !== 'N/A' && (
                                                            <Badge bg="secondary" className="fw-normal">{log.version}</Badge>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="align-middle text-nowrap">
                                                    {log.ip || '-'}
                                                </td>
                                                <td className="align-middle" style={{ minWidth: '300px', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                                    {log.mensaje}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>

                            {/* Infinite scroll trigger */}
                            <div ref={observerTarget} style={{ height: '30px', marginTop: '1rem' }}>
                                {isLoadingMore && (
                                    <div className="text-center py-3">
                                        <Spinner animation="border" size="sm" className="text-primary" />
                                        <span className="ms-2 text-primary">Cargando más logs...</span>
                                    </div>
                                )}
                                {!hasMore && logServicios.length > 0 && (
                                    <div className="text-center py-3 text-muted">
                                        <small>Has llegado al final de los logs.</small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    )
}

SeccionLogsAkasia.propTypes = {
    idLicencia: PropTypes.string,
    uuidLicencia: PropTypes.string
};

export default SeccionLogsAkasia;
